import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent } from '../components/ui/tabs';
import { LogOut, FileText, Dumbbell, Apple, Pill, Trophy, Heart, Activity, ArrowLeft } from 'lucide-react';
import { TCSLogo } from '../components/TCSLogo';
import { NavArrows } from '../components/NavArrows';

const Protocol = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const protocolSections = [
    {
      id: 'overview',
      title: 'Visão Geral',
      icon: FileText,
      content: {
        title: 'Protocolo TCS - Assessoria Esportiva',
        description: 'Protocolo completo de treinamento e nutrição para ciclistas de alto desempenho.',
        highlights: [
          'Treinos estruturados: Força, Potência, Prevenção, Velocidade e Ativação',
          'Protocolos nutricionais para treinos longos e curtos',
          'Suplementação específica para ciclistas',
          'Preparação para competições',
          'Zonas de treinamento baseadas em PSE (Percepção Subjetiva de Esforço)'
        ]
      }
    },
    {
      id: 'training',
      title: 'Treinos',
      icon: Dumbbell,
      content: {
        title: 'Protocolos de Treinamento',
        sections: [
          {
            name: '1. Prevenção Ciático',
            description: '9 exercícios focados em prevenção, cada um com 1 minuto de sustentação. Transição imediata entre exercícios.',
            frequency: '1x por semana'
          },
          {
            name: '2. Alongamento Específico',
            description: 'Rotina de alongamento direcionada para ciclistas.',
            frequency: 'Conforme necessidade'
          },
          {
            name: '3. Treino Academia Padrão',
            description: 'Exercícios de força: Agachamento Livre, Cadeira Extensora, Agachamento Hack, Stiff, Agachamento Unilateral, Cadeira Flexora.',
            params: 'Carga: 70-85% | Repetições: 8-12 | Descanso: 90-120s'
          },
          {
            name: '4. Treino Específico 01',
            description: 'Circuito de exercícios funcionais: Flexão de Braços, Agachamento, Abdominal. Transições imediatas.',
            note: 'Não pode ser feito em conjunto com outros treinos'
          },
          {
            name: '5. Treino Avançado',
            description: '10 treinos individualizados focados em força e potência máxima.',
            params: 'Carga: Elevada | Velocidade: Controlada | Ordem: Específica'
          },
          {
            name: '6. Prevenção Avançado',
            description: '13 exercícios de prevenção avançada com foco em estabilização e fortalecimento.',
            params: 'Carga: Corporal | Repetições: Variadas'
          },
          {
            name: '7. Diafragma + Core',
            description: 'Rotina matinal de fortalecimento do core e diafragma para melhor desempenho respiratório.',
            frequency: 'Diariamente pela manhã'
          }
        ]
      }
    },
    {
      id: 'nutrition',
      title: 'Nutrição',
      icon: Apple,
      content: {
        title: 'Protocolo Nutricional - Longo x Curto',
        subtitle: 'Protocolo básico alimentar para treinos longos e curtos (Ciclistas)',
        sections: [
          {
            name: 'Treinos até 2:00h (CURTOS)',
            highlight: true,
            items: [
              'Tempo Nutricional: 45 minutos',
              'Carboidratos: 35 gramas por porção',
              'Tempo de Hidratação: 1 hora',
              'Hidratação: 1 garrafa de 700-800ml',
              'Fracionar: pequenos goles até completar a garrafa em 1 hora',
              'Comer e beber ANTES da necessidade (no tempo correto)',
              'Variar alimentos - não repetir os mesmos'
            ]
          },
          {
            name: 'Treinos acima de 2:00h (LONGOS)',
            highlight: true,
            items: [
              'Tempo Nutricional: 45min → diminui 5min a cada intervalo até 30min',
              'Exemplo: 1ª aos 45min → 2ª aos 40min → 3ª aos 35min → depois a cada 30min',
              'Carboidratos: 35g por porção, variando a substância',
              'Tempo de Hidratação: 50 minutos a 1 hora',
              'Hidratação: 1 garrafa de 700-800ml OU MAIS com outras substâncias',
              'Priorizar água + eletrólitos em outra garrafa',
              'Consumir bem mais de 700-800ml por hora',
              'Suplementos: Beta Alanina, BCAA e outros junto com hidratação/nutrição',
              'Variar alimentos constantemente'
            ]
          },
          {
            name: 'Pré-Treino (40min antes)',
            items: [
              'Preferir comidas líquidas',
              'Ricas em proteínas e carboidratos',
              'Evitar alimentos pesados',
              'Hidratação adequada antes de sair'
            ]
          },
          {
            name: 'Intra-Treino',
            items: [
              '30-40g de carboidratos a cada 50 minutos',
              'Fontes: Paçoca, rapadura, amendoim, gel, maltodextrina',
              'Palatinose, maltodextrose, Reporsalt',
              'Hidratação: água + eletrólitos',
              'Treinar o sistema digestivo'
            ]
          },
          {
            name: 'Pós-Treino (3 etapas de recuperação)',
            items: [
              'Imediato: reposição rápida (carbo + proteína)',
              'Até 30min: shake ou refeição líquida',
              'Refeição sólida: ovos, batata doce, peito de frango',
              'Priorizar proteína + menos carboidratos',
              'Reidratação: 150% do peso perdido'
            ]
          },
          {
            name: 'Protocolo Semana de Prova',
            items: [
              'MANHÃ (em jejum): 500ml suco de beterraba sem açúcar',
              '+ 5g Beta Alanina + 10g Creatina + 5g Glutamina',
              'APÓS CAFÉ: 1g Vitamina C',
              '+ 1 sachê Eno Guaraná',
              '+ 1 sachê ou cápsula de NAC (Acetilcisteína)',
              '⚠ SEMPRE consulte seu nutricionista antes'
            ]
          },
          {
            name: 'Equivalências Práticas (para o bolso)',
            items: [
              'Gel de Carboidrato: 20-25g',
              'Dose de Isotônico: 30-35g',
              'Banana Média: 25g',
              'Paçoca: 12g',
              'Barra de Cereal / Goiabinha: 15-20g',
              'Palatinose (1 scoop): 15g'
            ]
          },
          {
            name: 'Diferença CURTO x LONGO',
            items: [
              'CURTOS: ingestão espaçada (45min nutrição / 1h hidratação)',
              'LONGOS: ingestão mais frequente (30-45min nutrição / 50-60min hidratação)',
              'Quantidade por porção (35g) é a mesma',
              'Frequência de consumo é maior em treinos longos',
              'Longos: hidratação mais robusta com eletrólitos',
              'Longos: incluir suplementação durante o exercício'
            ]
          }
        ]
      }
    },
    {
      id: 'supplements',
      title: 'Suplementação',
      icon: Pill,
      content: {
        title: 'Suplementação Básica para Ciclistas',
        warning: 'SEMPRE consulte um nutricionista antes de iniciar qualquer suplementação!',
        supplements: [
          { name: 'Beta Alanina', benefit: 'Reduz fadiga muscular, melhora performance em esforços intensos' },
          { name: 'Creatina', benefit: 'Aumenta força e potência, melhora recuperação' },
          { name: 'BCAAs', benefit: 'Reduz catabolismo muscular, auxilia recuperação' },
          { name: 'Whey Protein', benefit: 'Síntese proteica, recuperação muscular' },
          { name: 'Cafeína', benefit: 'Melhora foco, reduz percepção de esforço' },
          { name: 'Ômega 3', benefit: 'Anti-inflamatório, saúde cardiovascular' },
          { name: 'Vitamina D', benefit: 'Função imunológica, saúde óssea' },
          { name: 'Magnésio', benefit: 'Função muscular, prevenção de cãibras' },
          { name: 'Zinco', benefit: 'Sistema imunológico, recuperação' },
          { name: 'Multivitamínico', benefit: 'Suporte nutricional geral' }
        ]
      }
    },
    {
      id: 'race',
      title: 'Pré-Prova',
      icon: Trophy,
      content: {
        title: 'Preparatório Pré-Prova',
        weekProtocol: [
          {
            day: 'Segunda a Quarta',
            protocol: [
              'Manter treinos leves de ativação',
              'Aumentar ingestão de carboidratos (7-10g/kg)',
              'Hidratação adequada',
              'Reduzir fibras e alimentos pesados',
              'Dormir 8-9 horas'
            ]
          },
          {
            day: 'Quinta',
            protocol: [
              'Treino curtíssimo de ativação',
              'Carboidratos: 8-10g/kg',
              'Evitar alimentos novos',
              'Organizar equipamentos'
            ]
          },
          {
            day: 'Sexta',
            protocol: [
              'Descanso completo ou pedalada leve 30min',
              'Carboidratos: 10-12g/kg',
              'Hidratação com eletrólitos',
              'Jantar leve às 19h'
            ]
          },
          {
            day: 'Dia da Prova',
            protocol: [
              '3-4h antes: Café da manhã com carboidratos (aveia, banana, mel)',
              '1h antes: Gel + 500ml água',
              '15min antes: Último gel + hidratação',
              'Durante: Protocolo de reposição energética'
            ]
          }
        ],
        raceDay: {
          title: 'Protocolo Semana de Prova - Específico',
          warning: 'Protocolo avançado! Consulte seu nutricionista.',
          items: [
            'Suplementação específica conforme orientação',
            'Timing preciso de carboidratos',
            'Teste prévio em treinos',
            'Ajuste conforme duração e intensidade da prova'
          ]
        }
      }
    },
    {
      id: 'zones',
      title: 'Zonas Treino',
      icon: Activity,
      content: {
        title: 'Tabela de Percepção Subjetiva de Esforço (PSE)',
        description: 'Sistema de zonas baseado na percepção de esforço, fundamental para treinos sem medidor de potência.',
        zones: [
          {
            zone: 'Zona 1',
            pse: '1-2',
            description: 'Recuperação Ativa',
            hr: 'Até 68% FCMax',
            feeling: 'Muito fácil, consegue conversar normalmente'
          },
          {
            zone: 'Zona 2',
            pse: '3-4',
            description: 'Endurance / Fundo',
            hr: '69-83% FCMax',
            feeling: 'Confortável, conversa com frases longas'
          },
          {
            zone: 'Zona 3',
            pse: '5-6',
            description: 'Tempo / Ritmo',
            hr: '84-94% FCMax',
            feeling: 'Moderado a intenso, frases curtas'
          },
          {
            zone: 'Zona 4',
            pse: '7-8',
            description: 'Limiar Anaeróbico',
            hr: '95-100% FCMax',
            feeling: 'Muito intenso, poucas palavras'
          },
          {
            zone: 'Zona 5',
            pse: '9',
            description: 'VO2 Max',
            hr: '100%+ FCMax',
            feeling: 'Extremo, não consegue falar'
          },
          {
            zone: 'Zona 6',
            pse: '10',
            description: 'Potência Anaeróbica',
            hr: 'Máximo',
            feeling: 'Esforço máximo, sprints'
          }
        ],
        notes: [
          'PSE é uma ferramenta confiável para regular intensidade',
          'Use a percepção + FC para validar sua zona',
          'Consistência é mais importante que perfeição'
        ]
      }
    },
    {
      id: 'monitoring',
      title: 'Monitoramento',
      icon: Heart,
      content: {
        title: 'Coleta de Dados e Monitoramento',
        sections: [
          {
            name: 'FC Repouso',
            description: 'Medição da Frequência Cardíaca de Repouso',
            steps: [
              '1. Ao acordar, ainda deitado',
              '2. Antes de olhar celular ou se movimentar',
              '3. Relaxe por 2-3 minutos',
              '4. Meça FC por 1 minuto completo',
              '5. Registre o valor',
              '6. Compare com sua média semanal'
            ],
            interpretation: [
              'FC repouso elevada (+5-10bpm) = Possível overtraining ou doença',
              'FC repouso baixa = Boa adaptação ao treino',
              'Variações normais: ±3bpm'
            ]
          },
          {
            name: 'Variáveis Importantes',
            metrics: [
              'CTL (Chronic Training Load): Carga de treino de longo prazo',
              'TSS (Training Stress Score): Estresse acumulado',
              'NP (Normalized Power): Potência normalizada',
              'IF (Intensity Factor): Fator de intensidade',
              'Wellness Score: Bem-estar diário (já registrado no app)'
            ]
          }
        ]
      }
    }
  ];

  const currentSection = protocolSections.find(s => s.id === activeTab);

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-black/60 backdrop-blur-xl border-b border-white/10">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <NavArrows />
            <TCSLogo size="sm" showTagline={false} />
            <div>
              <h1 className="text-xl md:text-2xl font-black uppercase tracking-tighter text-[#00F0FF]" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                PROTOCOLO TCS
              </h1>
              <p className="text-xs tracking-[0.2em] uppercase text-[#A1A1AA]">
                Treinamento e Nutrição
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate('/dashboard')}
              variant="outline"
              className="bg-transparent border-[#00F0FF] text-[#00F0FF] hover:bg-[#00F0FF]/10 rounded-none hidden md:inline-flex"
              data-testid="protocol-back-to-dashboard"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Dashboard
            </Button>
            <Button
              onClick={handleLogout}
              variant="outline"
              size="icon"
              className="bg-transparent border-[#FF3366] text-[#FF3366] hover:bg-[#FF3366]/10 rounded-none"
              data-testid="protocol-logout-button"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8" data-testid="protocol-main">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          {/* Tab Navigation */}
          <div className="w-full grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2 bg-[#121212] border border-white/10 p-2 mb-8">
            {protocolSections.map((section) => {
              const Icon = section.icon;
              const isActive = activeTab === section.id;
              return (
                <button
                  key={section.id}
                  type="button"
                  onClick={() => setActiveTab(section.id)}
                  className={`flex items-center justify-center gap-2 px-3 py-3 rounded-none transition-colors ${
                    isActive
                      ? 'bg-[#00F0FF] text-black'
                      : 'text-[#A1A1AA] hover:text-white hover:bg-white/5'
                  }`}
                  data-testid={`protocol-tab-${section.id}`}
                >
                  <Icon className="h-4 w-4 flex-shrink-0" />
                  <span className="text-[10px] md:text-xs uppercase font-bold tracking-wider whitespace-nowrap truncate">{section.title}</span>
                </button>
              );
            })}
          </div>

          {/* Tab Content */}
          {protocolSections.map((section) => (
            <TabsContent key={section.id} value={section.id} className="mt-0">
              <div className="bg-[#121212] border border-white/10 p-8 shadow-[inset_0_1px_0_rgba(255,255,255,0.05)]">
                {/* Overview */}
                {section.id === 'overview' && (
                  <div className="space-y-6">
                    <p className="text-lg text-white">{section.content.description}</p>
                    
                    <div className="bg-[#0A0A0A] p-6 border border-white/5 mt-6">
                      <h3 className="text-xl font-bold uppercase tracking-tight text-[#00F0FF] mb-4" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                        Destaques do Protocolo
                      </h3>
                      <ul className="space-y-3">
                        {section.content.highlights.map((item, idx) => (
                          <li key={idx} className="flex items-start gap-3">
                            <div className="w-1.5 h-1.5 bg-[#00F0FF] mt-2 flex-shrink-0"></div>
                            <p className="text-white">{item}</p>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="bg-[#00F0FF]/10 border border-[#00F0FF] p-6 mt-6">
                      <h3 className="text-lg font-bold text-[#00F0FF] mb-2">Protocolo Completo no App</h3>
                      <p className="text-white mb-4">Todo o conteúdo do protocolo está disponível nas abas acima: Treinos, Nutrição, Suplementação, Pré-Prova, Zonas de Treino e Monitoramento. Navegue pelas abas para acessar todas as informações.</p>
                      <div className="flex items-start gap-2 text-sm text-[#A1A1AA]">
                        <FileText className="w-4 h-4 flex-shrink-0 mt-0.5" />
                        <span>Os vídeos e ilustrações detalhadas estão disponíveis diretamente com o treinador Thyago Costa Silva.</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Training */}
                {section.id === 'training' && (
                  <div className="space-y-6">
                    <h2 className="text-3xl font-black uppercase tracking-tight text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                      {section.content.title}
                    </h2>
                    <div className="space-y-4">
                      {section.content.sections.map((item, idx) => (
                        <div key={idx} className="bg-[#0A0A0A] p-6 border border-white/5">
                          <h3 className="text-xl font-bold text-[#00F0FF] mb-2">{item.name}</h3>
                          <p className="text-white mb-3">{item.description}</p>
                          {item.params && (
                            <p className="text-sm text-[#A1A1AA] font-mono bg-[#121212] p-2 border border-white/10">{item.params}</p>
                          )}
                          {item.frequency && (
                            <p className="text-sm text-[#39FF14] mt-2">Frequência: {item.frequency}</p>
                          )}
                          {item.note && (
                            <p className="text-sm text-[#FFB800] mt-2 italic">⚠️ {item.note}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Nutrition */}
                {section.id === 'nutrition' && (
                  <div className="space-y-6">
                    <div>
                      <h2 className="text-3xl font-black uppercase tracking-tight text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                        {section.content.title}
                      </h2>
                      {section.content.subtitle && (
                        <p className="text-sm text-[#A1A1AA] mt-2">{section.content.subtitle}</p>
                      )}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {section.content.sections.map((item, idx) => (
                        <div 
                          key={idx} 
                          className={`p-6 border ${item.highlight ? 'border-[#00F0FF] bg-[#00F0FF]/5 md:col-span-1' : 'border-white/5 bg-[#0A0A0A]'}`}
                        >
                          <h3 className={`text-lg font-bold mb-3 ${item.highlight ? 'text-[#00F0FF]' : 'text-[#00F0FF]'}`} style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                            {item.name}
                          </h3>
                          <ul className="space-y-2">
                            {item.items.map((point, pidx) => (
                              <li key={pidx} className="flex items-start gap-2">
                                <span className="text-[#00F0FF] mt-1 flex-shrink-0">•</span>
                                <span className="text-white text-sm">{point}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Supplements */}
                {section.id === 'supplements' && (
                  <div className="space-y-6">
                    <h2 className="text-3xl font-black uppercase tracking-tight text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                      {section.content.title}
                    </h2>
                    <div className="bg-[#FFB800]/10 border border-[#FFB800] p-4 mb-6">
                      <p className="text-[#FFB800] font-bold">⚠️ {section.content.warning}</p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {section.content.supplements.map((supp, idx) => (
                        <div key={idx} className="bg-[#0A0A0A] p-4 border border-white/5">
                          <h3 className="text-lg font-bold text-[#00F0FF] mb-2">{supp.name}</h3>
                          <p className="text-sm text-white">{supp.benefit}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Race */}
                {section.id === 'race' && (
                  <div className="space-y-6">
                    <h2 className="text-3xl font-black uppercase tracking-tight text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                      {section.content.title}
                    </h2>
                    <div className="space-y-4">
                      {section.content.weekProtocol.map((item, idx) => (
                        <div key={idx} className="bg-[#0A0A0A] p-6 border border-white/5">
                          <h3 className="text-xl font-bold text-[#00F0FF] mb-3">{item.day}</h3>
                          <ul className="space-y-2">
                            {item.protocol.map((point, pidx) => (
                              <li key={pidx} className="flex items-start gap-3">
                                <div className="w-1.5 h-1.5 bg-[#00F0FF] mt-2 flex-shrink-0"></div>
                                <p className="text-white">{point}</p>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                    <div className="bg-[#FF3366]/10 border border-[#FF3366] p-6 mt-6">
                      <h3 className="text-lg font-bold text-[#FF3366] mb-2">{section.content.raceDay.title}</h3>
                      <p className="text-[#FF3366] font-bold mb-3">⚠️ {section.content.raceDay.warning}</p>
                      <ul className="space-y-2">
                        {section.content.raceDay.items.map((item, idx) => (
                          <li key={idx} className="text-white text-sm flex items-start gap-2">
                            <span className="text-[#FF3366]">•</span>
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}

                {/* Zones */}
                {section.id === 'zones' && (
                  <div className="space-y-6">
                    <h2 className="text-3xl font-black uppercase tracking-tight text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                      {section.content.title}
                    </h2>
                    <p className="text-lg text-[#A1A1AA]">{section.content.description}</p>
                    <div className="space-y-3">
                      {section.content.zones.map((zone, idx) => (
                        <div key={idx} className="bg-[#0A0A0A] p-5 border border-white/5 hover:border-[#00F0FF]/30 transition-colors">
                          <div className="flex items-start justify-between mb-3">
                            <div>
                              <h3 className="text-lg font-bold text-[#00F0FF]">{zone.zone} (PSE {zone.pse})</h3>
                              <p className="text-white font-semibold">{zone.description}</p>
                            </div>
                            <span className="text-xs text-[#A1A1AA] bg-[#121212] px-3 py-1 border border-white/10">{zone.hr}</span>
                          </div>
                          <p className="text-sm text-[#A1A1AA] italic">{zone.feeling}</p>
                        </div>
                      ))}
                    </div>
                    <div className="bg-[#00F0FF]/10 border border-[#00F0FF] p-4 mt-6">
                      <ul className="space-y-2">
                        {section.content.notes.map((note, idx) => (
                          <li key={idx} className="text-white text-sm flex items-start gap-2">
                            <span className="text-[#00F0FF]">•</span>
                            {note}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}

                {/* Monitoring */}
                {section.id === 'monitoring' && (
                  <div className="space-y-6">
                    <h2 className="text-3xl font-black uppercase tracking-tight text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                      {section.content.title}
                    </h2>
                    {section.content.sections.map((sec, idx) => (
                      <div key={idx} className="bg-[#0A0A0A] p-6 border border-white/5 space-y-4">
                        <h3 className="text-xl font-bold text-[#00F0FF]">{sec.name}</h3>
                        <p className="text-white">{sec.description}</p>
                        {sec.steps && (
                          <div>
                            <h4 className="text-sm font-bold text-[#A1A1AA] uppercase mb-2">Como Medir:</h4>
                            <ul className="space-y-2">
                              {sec.steps.map((step, sidx) => (
                                <li key={sidx} className="text-white text-sm pl-4">{step}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {sec.interpretation && (
                          <div>
                            <h4 className="text-sm font-bold text-[#A1A1AA] uppercase mb-2">Interpretação:</h4>
                            <ul className="space-y-2">
                              {sec.interpretation.map((point, pidx) => (
                                <li key={pidx} className="text-white text-sm flex items-start gap-2">
                                  <span className="text-[#00F0FF]">•</span>
                                  {point}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {sec.metrics && (
                          <div>
                            <h4 className="text-sm font-bold text-[#A1A1AA] uppercase mb-2">Métricas:</h4>
                            <ul className="space-y-2">
                              {sec.metrics.map((metric, midx) => (
                                <li key={midx} className="text-white text-sm flex items-start gap-2">
                                  <span className="text-[#00F0FF]">•</span>
                                  {metric}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </main>
    </div>
  );
};

export default Protocol;